/**
 * Phase M follow-up — Verification health chip.
 *
 * Renders inside the AdminShell header. Pulls from
 * GET /api/admin/catalog/verification-audit on mount + every 60s.
 * Only rendered for role=admin (AdminShell already gates the whole tree).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const REFRESH_MS = 60 * 1000;

export default function VerificationHealthChip({ role }) {
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (role !== "admin") return undefined;
    let alive = true;
    const load = () => {
      api.get("/admin/catalog/verification-audit")
        .then((r) => { if (alive) { setAudit(r.data); setError(false); } })
        .catch(() => { if (alive) setError(true); });
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, [role]);

  if (role !== "admin") return null;

  if (error) {
    return (
      <div
        className="ml-auto flex items-center gap-2 border border-crimson/40 bg-crimson/10 px-3 py-1.5 text-crimson"
        data-testid="admin-verification-chip"
        data-status="error"
      >
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold">Audit unavailable</span>
      </div>
    );
  }

  if (!audit) {
    return (
      <div
        className="ml-auto flex items-center gap-2 border border-stone/40 bg-bone px-3 py-1.5"
        data-testid="admin-verification-chip"
        data-status="loading"
      >
        <span className="text-[10px] uppercase tracking-[0.22em] text-stone">Loading…</span>
      </div>
    );
  }

  const inst = audit.institutions?.verified ?? 0;
  const majors = audit.majors?.verified ?? 0;
  const roadmaps = audit.roadmaps?.verified ?? 0;
  const programs = audit.programs?.verified ?? 0;
  const sources = audit.verification_records?.total ?? 0;
  const pendingTotal = (
    (audit.institutions?.awaiting || 0) + (audit.institutions?.draft || 0) +
    (audit.majors?.awaiting || 0) + (audit.majors?.draft || 0) +
    (audit.roadmaps?.awaiting || 0) + (audit.roadmaps?.draft || 0) +
    (audit.verification_records?.awaiting || 0) +
    (audit.public_ui_exposure?.community_colleges_placeholder || 0) +
    (audit.public_ui_exposure?.majors_placeholder || 0) +
    (audit.public_ui_exposure?.roadmaps_placeholder || 0)
  );

  const pendingCls = pendingTotal > 0 ? "text-crimson font-semibold" : "text-bone-dim";

  return (
    <button
      type="button"
      onClick={() => navigate("/admin/verification")}
      className="ml-auto flex items-center gap-2 border border-brass bg-ivy text-bone px-3 py-1.5 hover:bg-brass hover:text-ivy transition-colors"
      data-testid="admin-verification-chip"
      data-status={pendingTotal > 0 ? "warning" : "ok"}
      title="Verification health — click for full audit view"
    >
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brass-soft group-hover:text-ivy" aria-hidden="true">Health</span>
      <span className="h-3 w-px bg-brass-soft/60" aria-hidden="true"></span>
      <span className="text-[10px] tracking-[0.05em] normal-case text-bone">
        <span data-testid="chip-institutions">{inst}</span>&nbsp;institutions
        <span className="text-brass-soft mx-1.5" aria-hidden="true">·</span>
        <span data-testid="chip-majors">{majors}</span>&nbsp;majors
        <span className="text-brass-soft mx-1.5" aria-hidden="true">·</span>
        <span data-testid="chip-roadmaps">{roadmaps}</span>&nbsp;roadmaps
        <span className="text-brass-soft mx-1.5" aria-hidden="true">·</span>
        <span data-testid="chip-programs">{programs}</span>&nbsp;programs
        <span className="text-brass-soft mx-1.5" aria-hidden="true">·</span>
        <span data-testid="chip-sources">{sources}</span>&nbsp;sources
        <span className="text-brass-soft mx-1.5" aria-hidden="true">·</span>
        {pendingTotal > 0 && (
          <svg
            xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 20 20"
            fill="currentColor" aria-hidden="true"
            className="inline-block -mt-px mr-0.5 text-crimson"
          >
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        )}
        <span data-testid="chip-pending" className={pendingCls}>{pendingTotal}</span>&nbsp;pending
      </span>
    </button>
  );
}
